import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import {CSSObject, styled, Theme} from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import {observer} from 'mobx-react-lite';
import * as React from 'react';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useRouteMatch} from 'react-router-dom';

import {SUB_NAV_ITEM_ACCOUNT, SUB_NAV_ITEM_CREATE_PATIENT, SUB_NAV_ITEM_MY_PATIENTS} from '../../configs/sub-nav-items';
import {usePatients} from '../../hooks/patients/use-patients';
import {useAllUnits} from '../../hooks/units/use-all-units';
import {useStores} from '../../hooks/use-stores';
import {Unit} from '../../interfaces/entities/unit.interface';
import {getProductUrl} from '../../interfaces/products';
import {PRODUCTS} from '../../interfaces/products/products.enum';
import {SubNavItem} from '../../interfaces/sub-nav-item.interface';
import {DEPARTMENT_UNIT_TYPE} from '../../stores/units.store';
import {LOCALES} from '../../utils/constants';
import {isUserAdmin} from '../../utils/is-user-admin';
import {mapVilmerLanguage} from '../../utils/map-vilmer-language';
import {SidebarItemComponent} from './sidebar-item.component';

const SIDEBAR_WIDTH = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: SIDEBAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {shouldForwardProp: prop => prop !== 'open'})(({theme}) => ({
  width: SIDEBAR_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({open}) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({open}) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

function Sidebar() {
  const {sidebarStore, authStore} = useStores();
  const {path} = useRouteMatch();
  const {i18n} = useTranslation();

  const {items, loading, patientsWithNewGuestbookPosts} = usePatients();
  const {items: units = [], loading: loadingUnits} = useAllUnits();

  const handleDrawerOpen = useCallback(() => {
    sidebarStore.setIsOpen(true);
  }, [sidebarStore]);

  const handleDrawerClose = useCallback(() => {
    sidebarStore.setIsOpen(false);
  }, [sidebarStore]);

  const isAdmin = useMemo(() => {
    return authStore.me && isUserAdmin(authStore.me);
  }, [authStore.me]);

  const departments: Unit[] = useMemo(() => {
    const n = units.length;
    if (loadingUnits !== false || !n) {
      return [];
    }

    const arr: Unit[] = [];
    for (let i = 0; i < n; i++) {
      if (units[i].type === DEPARTMENT_UNIT_TYPE) {
        arr.push(units[i]);
      }
    }

    return arr;
  }, [loadingUnits, units]);

  const navItems = useMemo(() => {
    const arr: SubNavItem[] = [];

    if (departments.length > 0) {
      arr.unshift(SUB_NAV_ITEM_CREATE_PATIENT);
    }

    if (loading == false && (items.length > 1 || isAdmin)) {
      arr.unshift({
        ...SUB_NAV_ITEM_MY_PATIENTS,
        counter: patientsWithNewGuestbookPosts,
      });
    }

    return arr;
  }, [departments.length, isAdmin, items.length, loading, patientsWithNewGuestbookPosts]);

  const accountNavItem = useMemo(() => {
    const url = new URL(getProductUrl(PRODUCTS.Dashboard));
    const vilmerLng = mapVilmerLanguage(i18n.language as LOCALES);
    if (vilmerLng) {
      url.pathname = `/${vilmerLng}/account`;
    }

    SUB_NAV_ITEM_ACCOUNT.href = url.toString();

    return SUB_NAV_ITEM_ACCOUNT;
  }, [i18n.language]);

  if (!authStore.me) {
    return;
  }

  return (
    <Drawer variant="permanent" open={sidebarStore.isOpen}>
      <Toolbar style={{height: '118px'}} />
      {/*<DrawerHeader>*/}
      {/*  <IconButton onClick={sidebarStore.isOpen ? handleDrawerClose : handleDrawerOpen}>*/}
      {/*    {sidebarStore.isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}*/}
      {/*  </IconButton>*/}
      {/*</DrawerHeader>*/}
      {/*<Divider />*/}
      <List>
        {navItems.map((item, index) => (
          <SidebarItemComponent item={item} isOpen={sidebarStore.isOpen} isActive={path === item.to} key={index} />
        ))}
      </List>
    </Drawer>
  );
}

export const SidebarComponent = observer(Sidebar);
